/**
 * Service centralisé pour les appels API du portail client
 */
import api from './api';

export interface BuyerProfile {
  id: number;
  company_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  is_active: boolean;
  status: 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme';
  client_type: string;
  member_type?: string | null;  // Type de membre si connecté en tant que membre
  parent_buyer_id?: number | null;  // ID du Client parent si membre
  bpu_locked?: boolean;  // BPU verrouillé
  admin_id: number;
  created_at: string;
  updated_at: string;
  last_login: string | null;
}

export interface BuyerOrder {
  id: number;
  client_id: number;
  order_number: string;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'invoiced' | 'paid' | 'cancelled';
  subtotal: number;
  tax_amount: number;
  delivery_fee: number;
  total_amount: number;
  credit_note_amount?: number;
  currency: string;
  order_date: string;
  created_at: string;
  delivery_date: string | null;
  paid_at?: string | null;
  paid_by_admin_id?: number | null;
  enregistrer_at?: string | null;
  enregistrer_by_admin_id?: number | null;
  pdf_url?: string | null;
  delivered_at?: string | null;
  received_at?: string | null;
  notes: string | null;
  tier_breakdown?: string | null;
  // Delivery address fields
  delivery_address?: string | null;
  delivery_city?: string | null;
  delivery_postal_code?: string | null;
  delivery_country?: string | null;
  invoice_email?: string | null;
  // Tracking fields
  tracking_number?: string;
  current_location?: string;
  estimated_delivery_date?: string;
  tracking_notes?: string;
  last_tracking_update?: string;
  items?: Array<{
    id: number;
    order_id: number;
    consumable_id: number;
    quantity: number;
    quantity_delivered?: number;
    unit_price: number;
    discount_percentage: number;
    line_total: number;
    product_title: string;
    product_unit: string;
    batch_size: number;
    created_at: string;
    consumable?: {
      title: string;
      unit: string;
    };
  }>;
}

export interface BuyerDashboard {
  total_orders: number;
  total_spent: number;
  orders_by_status: Record<string, number>;
  pending_orders: number;
  orders_this_month: number;
  recent_orders: Array<{
    id: number;
    order_number: string;
    order_date: string;
    created_at?: string;
    status: string;
    subtotal?: number;
    total_amount: number;
    currency: string;
  }>;
}

export interface BuyerInvoice {
  id: number;
  invoice_number: string;
  order_id: number;
  amount: number;
  currency: string;
  status: string;
  issue_date: string;
  due_date: string;
}

// ========== PROFIL ==========

export const getMyProfile = async (): Promise<BuyerProfile> => {
  const response = await api.get('/clients/me');
  return response.data;
};

export const updateMyProfile = async (data: Partial<BuyerProfile>): Promise<BuyerProfile> => {
  const response = await api.patch('/clients/me', data);
  return response.data;
};

// ========== COMMANDES ==========

export const listMyOrders = async (params?: {
  skip?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<BuyerOrder[]> => {
  const response = await api.get('/clients/me/orders', { params });
  return response.data;
};

export const getMyOrder = async (orderId: number): Promise<BuyerOrder> => {
  const response = await api.get(`/clients/me/orders/${orderId}`);
  return response.data;
};

// ========== DASHBOARD ==========

export const getMyDashboard = async (): Promise<BuyerDashboard> => {
  const response = await api.get('/clients/me/dashboard');
  return response.data;
};

// ========== FACTURES ==========

export const listMyInvoices = async (params?: {
  skip?: number;
  limit?: number;
}): Promise<BuyerInvoice[]> => {
  const response = await api.get('/clients/me/invoices', { params });
  return response.data.invoices || [];
};

export const downloadInvoicePdf = async (invoiceId: number): Promise<Blob> => {
  const response = await api.get(`/clients/me/invoices/${invoiceId}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

// ========== CATALOGUE ==========

export const listConsumables = async (params?: {
  skip?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}): Promise<any[]> => {
  const response = await api.get('/consumables', { params: { ...params, is_active: true } });
  return response.data;
};

export default {
  getMyProfile,
  updateMyProfile,
  listMyOrders,
  getMyOrder,
  getMyDashboard,
  listMyInvoices,
  downloadInvoicePdf,
  listConsumables,
};
